import { IBaseResponse, fetchWrapper } from './apiBase';
import { ICommit, IRepository, IResourceVersion, IEntityUser } from '@/types';
import { BASE_API_URL } from '@/config/environment';
// import { cleanQueryStringFromObject } from '@/lib/querystring';
import { IDataFrameResponse } from '@/lib/dataFrameTypes';
import { IRemoteWorkspace } from '@/lib/workspaces/workspaceApi';
import { TEvaluationModel } from '@/lib/evaluationModelsApi';

export type TEvaluationStatus = 'new' | 'queued' | 'started' | 'completed' | 'error' | 'cancelled' | 'unknown';
export type TEvaluationInputType = 'text' | 'embeddings' | 'image';
export type TEvaluationOutputType = TEvaluationInputType;
type TEvaluationTargetType = 'commit' | 'workspace';
const getUrlBase = (repository: IRepository) => {
  return `${BASE_API_URL}/repos/${repository.namespace}/${repository.name}/evaluations`;
};

export interface IEvaluation {
  id: string;
  commit_id: string;
  closing_commit?: ICommit;
  credits_used?: string;
  conflict_target_branch?: string | null;
  created_by: IEntityUser;
  inserted_at: string;
  queued_at: string;
  started_at: string | null;
  completed_at: string | null;
  is_sample: boolean;
  sample_count: number;
  iterations_count?: number;
  model: TEvaluationModel;
  model_id?: string;
  master_id: string;
  parent_id?: string;
  progress: {
    processed: number;
    total: number;
  };
  prompt: string;
  name: string;
  repository_id: string;
  resource: IResourceVersion;
  status: TEvaluationStatus;
  target_column: string;
  target_path: string;
  target_branch: string;
  tokens_used?: number;
  prompt_tokens_used?: number;
  completion_tokens_used?: number;
  error_message?: string | null;
  auto_commit: boolean;
  commit_message?: string;
  // type: TEvaluationType;
  input_type: TEvaluationInputType;
  output_type: TEvaluationOutputType;
  target_type?: TEvaluationTargetType;
  workspace?: IRemoteWorkspace;
  batch_size?: number;
}

interface IEvaluationsResponse extends IBaseResponse {
  evaluations: IEvaluation[];
}

interface IEvaluationResponse extends IBaseResponse {
  evaluation: IEvaluation;
}

interface IBaseEvaluationsParams {
  repository: IRepository;
  token?: string;
}

export const fetchEvaluations = async ({
  repository,
  token,
}: IBaseEvaluationsParams): Promise<IEvaluationsResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = getUrlBase(repository);
  // console.log('fetchEvaluations', targetUrl);
  const data = await fetchWrapper({
    resourceName: 'evaluations',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
  });
  // console.log('fetchEvaluations > data', data);
  return data;
};

export const fetchEvaluationHistory = async ({
  repository,
  masterId,
  token,
}: IBaseEvaluationsParams & { masterId: string }): Promise<IEvaluationsResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = `${getUrlBase(repository)}?master_id=${masterId}`;
  // console.log('fetchEvaluations', targetUrl);
  const data = await fetchWrapper({
    resourceName: 'evaluationHistory',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName, masterId },
  });
  return data;
};

interface IFetchEvaluationParams extends IBaseEvaluationsParams {
  evaluationId: string;
}

export const fetchEvaluation = async ({
  repository,
  evaluationId,
  token,
}: IFetchEvaluationParams): Promise<IEvaluationResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = `${getUrlBase(repository)}/${evaluationId}`;
  // console.log('fetchEvaluation', targetUrl);
  const data = await fetchWrapper({
    resourceName: 'evaluation',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName, evaluationId },
  });
  // console.log('fetchEvaluation > data', data);
  return data;
};

export const fetchEvaluationSampleDataframe = async ({
  repository,
  evaluationId,
  token,
}: IFetchEvaluationParams): Promise<IDataFrameResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = `${getUrlBase(repository)}/${evaluationId}/data_frame`;
  // console.log('fetchEvaluation', targetUrl);
  const data = await fetchWrapper({
    resourceName: 'evaluationSampleDataframe',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName, evaluationId },
  });
  // console.log('fetchEvaluation > data', data);
  return data;
};

interface ICreateEvaluationParams extends IBaseEvaluationsParams {
  resource: IResourceVersion;
  name: string;
  prompt: string;
  input_type: TEvaluationInputType;
  output_type: TEvaluationOutputType;
  model_id: string;
  is_sample: boolean;
  sample_count?: number;
  target_column: string;
  target_branch: string;
  target_path: string;
  master_id?: string;
  parent_id?: string;
  auto_commit?: boolean;
  commit_message?: string;
  batch_size?: number;
}

export const createEvaluation = async ({
  repository,
  resource,
  name,
  prompt,
  input_type,
  output_type,
  model_id,
  is_sample,
  sample_count,
  target_column,
  target_branch,
  target_path,
  master_id,
  parent_id,
  auto_commit,
  commit_message,
  batch_size = 10,
  token,
}: ICreateEvaluationParams): Promise<IEvaluationResponse> => {
  const { namespace, name: repositoryName } = repository;
  const resourcePath = [resource.version, resource.path].join('/');
  // const resourcePath = resource.path;

  const targetUrl = `${getUrlBase(repository)}/${resourcePath}`;

  const body = {
    name,
    prompt,
    input_type,
    output_type,
    model_id,
    target_column,
    target_branch,
    is_sample,
    sample_count,
    target_path,
    parent_id,
    master_id,
    auto_commit,
    commit_message,
    batch_size,
  };

  // console.log('createEvaluation > body', body);

  const data = await fetchWrapper({
    resourceName: 'createEvaluation',
    method: 'POST',
    contentType: 'application/json',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
    body: JSON.stringify(body),
  });

  // console.log('createEvaluation > data', data);
  // const { evaluation } = data;
  // const { workspace_id } = evaluation;

  return data;
};

export const deleteEvaluation = async ({
  repository,
  evaluationId,
  token,
}: IFetchEvaluationParams): Promise<IBaseResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = `${getUrlBase(repository)}/${evaluationId}`;
  // console.log('fetchEvaluation', targetUrl);
  const data = await fetchWrapper({
    resourceName: 'evaluation',
    method: 'DELETE',
    contentType: 'application/json',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName, evaluationId },
  });
  // console.log('fetchEvaluation > data', data);
  return data;
};

export const cancelEvaluation = async ({
  repository,
  evaluationId,
  token,
}: IFetchEvaluationParams): Promise<IBaseResponse> => {
  const { namespace, name: repositoryName } = repository;
  const targetUrl = `${getUrlBase(repository)}/${evaluationId}`;
  const data = await fetchWrapper({
    resourceName: 'evaluation',
    method: 'PUT',
    contentType: 'application/json',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName, evaluationId },
    body: JSON.stringify({ status: 'cancelled' }),
  });
  // console.log('fetchEvaluation > data', data);
  return data;
};
